import { TextFieldProps } from "common/components";
import { CardsSelectionType, CardsSize } from "core/actions/flows/steps/Cards";
import { useTranslation } from "i18n";
import { isEqual } from "lodash";
import { ReactElement, useEffect } from "react";
import {
  FormControlledFieldProps,
  withFormController,
} from "../FormController";
import { SearchableCardOptionsField } from "./SearchableCardOptionsField/SearchableCardOptionsField";

export type FormCardsProps = Omit<TextFieldProps, "variant"> &
  FormControlledFieldProps & {
    renderValue?: (value: any) => any;
    /** List of options */
    options: any[];
    /** The selected indices. */
    selectedIndices: number[];
    /** Function to set the selected indices. */
    setSelectedIndices: (value: React.SetStateAction<number[]>) => void;
    /** If search and pagination should be enabled. */
    withSearch: boolean;
    /** Size of cards */
    cardSize: CardsSize;
    /** If single or multi select */
    selectionType: CardsSelectionType;
  };

/**
 * Adds form handling for Card options.
 *
 * @param fieldName Static string used for the unique ids of the inner components.
 * @param control Form control.
 * @param props Any remaining properties.
 *
 */
export const FormCards = withFormController<FormCardsProps>(
  ({
    fieldName,
    renderValue,
    renderState,
    selectedIndices,
    options,
    setSelectedIndices,
    withSearch,
    cardSize,
    selectionType,
    ...props
  }): ReactElement => {
    const { t } = useTranslation(["common"]);
    const {
      field: { value, onChange, ...field },
      fieldState: { error },
      setExtendedRules,
    } = renderState;

    /**
     * Triggers the fields onChange when the value changes
     * Needed for the validation to get the correct value
     */
    useEffect(() => {
      // Only update if the value is undefined or if selectedIndices and value are different
      if (value === undefined || !isEqual(value, selectedIndices)) {
        onChange(selectedIndices);
      }
    }, [selectedIndices, value, onChange]);

    useEffect(() => {
      setExtendedRules({
        validate: {
          notEmptyRequiredValue: (value: number[]) => {
            if (props.required && value.length < 1) {
              return false;
            }
            return true;
          },
        },
      });
    }, [setExtendedRules, t]);

    return (
      <>
        <SearchableCardOptionsField
          control={props.control}
          options={options}
          {...props}
          error={!!error}
          selectedIndices={selectedIndices}
          setSelectedIndices={setSelectedIndices}
          selectionType={selectionType}
          cardSize={cardSize}
          {...field}
        />
      </>
    );
  }
);
